import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam, AnyType } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { toLocaleString } from "./common";
import { getFile, IProtData } from "./getFIle";

type InternalFileRow = AnyType;

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: { Authorization: "Bearer " + token },
    });
  } else {
    return axios.get(url);
  }
}

interface IGetFilesResponceModel {
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * ファイルID
   */
  fileid: string;
  /**
   * ファイル名（オリジナル）
   */
  name: string;
  /**
   * 入力データ数
   */
  inchnum: number;
  /**
   * 出力データ数
   */
  outchnum: number;
  /**
   * データ数
   */
  datanum: number;
  /**
   * サンプリング周期[msec]
   */
  interval: number;
  /**
   * データの開始時刻
   */
  starttime: string;
  /**
   * データの終了時刻
   */
  endtime: string;
  /**
   * ファイル種別
   */
  filetype: string;
  /**
   * 最終更新時刻
   */
  update: string;
}

export interface IFileRow extends IGetFilesResponceModel {
  /**
   * 一連番号(DataGridで必須)
   */
  id: number;
}

export interface IProtRow extends IProtData {
  /**
   * 一連番号(IFileRow.id)
   */
  id: number;
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからファイル一覧の取得が成功した場合に呼び出されます
   * @callback success
   * @param {IFileRow[]} rows - ファイル一覧
   * @param {IProtRow[]} prots - チャートデータ一覧
   * @param {number} status - httpステータス
   */
  success: (rows: IFileRow[], prots: IProtRow[], status: number) => void;
}

interface IResolveData {
  row: IFileRow;
  prot: IProtData;
}

export interface GetFilesModel {
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * データ間引き閾値
   */
  threshold: number;
  /**
   * 取得するfileid
   */
  fileids?: string[];
}

interface IAxiosInternalParams extends GetFilesModel, ICallbackParams {
  promise: Promise<AxiosResponse>;
  authService?: AuthService;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      const datas: IGetFilesResponceModel[] = res.data;
      const rows: InternalFileRow[] = datas.slice();
      const fileids = params.fileids;
      const filterRows =
        fileids && fileids.length > 0
          ? rows.filter((row) => {
              return fileids.indexOf(row.fileid) !== -1;
            })
          : rows;
      const promises = filterRows.map((row, i) => {
        row.id = i;
        row.update = toLocaleString(new Date(Date.parse(row.update)));
        return new Promise<IResolveData>((resolve, reject) => {
          getFile({
            authService: params.authService,
            projectid: params.projectid,
            fileid: row.fileid,
            name: row.name,
            datanum: row.datanum,
            interval: row.interval,
            threshold: params.threshold,
            includeEnd: false,
            isRetry401: false,
            success: (prot) => {
              resolve({ row: row, prot: prot });
            },
            fail: (id, status) => {
              reject({ id, status });
            },
          });
        });
      });
      Promise.all(promises)
        .then((values) => {
          const rows: IFileRow[] = [];
          const prots: IProtRow[] = [];
          values.forEach((value) => {
            const { row, prot } = value;
            const { id } = row;
            const { fileid, fname, datanum, interval, input, output } = prot;
            rows.push(row);
            prots.push({
              id: id,
              fileid: fileid,
              fname: fname,
              datanum: datanum,
              interval: interval,
              input: input,
              output: output,
            });
          });
          params.success(rows, prots, 200);
        })
        .catch((err) => {
          const { id, status } = err;
          params.fail(id, status);
        });
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetFilesParams extends GetFilesModel, ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
}

/**
 * ファイルとチャートデータの一覧を取得します
 * @function getFiles
 * @param {IGetFilesParams} params - ファイル一覧取得パラメータ
 */
export function getFiles(params: IGetFilesParams): void {
  const { authService, projectid, threshold, fileids } = params;
  const url = WebAPI.GET_DATAFILES(projectid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          authService: authService,
          projectid: projectid,
          threshold: threshold,
          fileids: fileids,
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getFilesRetry401(authService, url, params, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      projectid: projectid,
      threshold: threshold,
      fileids: fileids,
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * ファイルとチャートデータの一覧を取得します
 * @function getFilesRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {GetFilesModel} model - ファイル取得モデル
 * @param {ICallbackParams} param - コールバックパラメータ
 */
export function getFilesRetry401(
  authService: AuthService,
  url: string,
  model: GetFilesModel,
  param: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        authService: authService,
        projectid: model.projectid,
        threshold: model.threshold,
        fileids: model.fileids,
        success: param.success,
        fail: param.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      param.fail(id, status);
    },
  });
}
